// ComposerAreaSplitter — Feb 19, 2026 (iteration 2).
//
// Wraps the entire bottom composer block on the public Q&A page. The
// horizontal line that previously separated the scrolling chat history
// from the composer (formerly a passive `border-t`) is now a draggable
// handle that resizes the composer area on BOTH desktop and mobile.
//
// As the composer area shrinks below natural content height, items
// disappear in a cascade so the input + send button are never hidden:
//
//   squeezeLevel 0 → everything visible (default)
//   squeezeLevel 1 → footer fine-print hidden ("Creator may review..." +
//                    "Report this silo")
//   squeezeLevel 2 → subscribe pill + tip button ALSO hidden — only the
//                    chat input + send button remain
//
// Persistence: SESSION-ONLY. Fresh page load resets to the natural
// content height. Owner drags do NOT save (this is critical — subscribe
// + tip are revenue drivers and we never want a permanently-hidden
// state to outlast a session).
//
// Isolation note: this file plus the wrapper call-site in PublicSilo.jsx
// are the entire scope of iteration 2. To roll back: delete this file
// + restore the inline `<div className="sticky bottom-0 border-t ...">`
// wrapper in PublicSilo.jsx. Squeeze-aware conditional rendering in
// PublicSilo also becomes dead code and can be unwrapped at that time.
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { usePublicPricing } from "@/lib/usePublicPricing";

// Pixel deltas below natural-content height that trigger each squeeze
// level. Empirically tuned for the My Silo composer's typical layout
// (textarea + pill + tip row + footer + padding).
const SQUEEZE_1_THRESHOLD_PX = 25;  // footer eats ~24px including margin
const SQUEEZE_2_THRESHOLD_PX = 70;  // pill + tip row together eat ~84px
const HINT_DURATION_MS = 3000;      // ↕ indicator fade-in window on first paint

export default function ComposerAreaSplitter({ children, className = "", style = {} }) {
  // Refs for measurement + drag tracking.
  const wrapperRef = useRef(null);
  const naturalHeightRef = useRef(null);
  const dragStartY = useRef(0);
  const dragStartHeight = useRef(0);
  const dragging = useRef(false);

  // Current composer area height. `null` = no override (use natural
  // content sizing). Once the user drags, this becomes a concrete px value.
  const [currentHeight, setCurrentHeight] = useState(null);

  // Squeeze level for the render-prop child to consume. Recomputed every
  // time currentHeight changes.
  const [squeezeLevel, setSqueezeLevel] = useState(0);

  // Feb 19, 2026 — fade-in ↕ hint on the divider. Globally toggleable in
  // BumpStudio. Shows for 3 seconds on first paint to teach users the
  // divider is draggable, then disappears.
  const pricing = usePublicPricing();
  const hintEnabled = pricing.composer_divider_hint_enabled !== false;
  const [hintVisible, setHintVisible] = useState(false);
  useEffect(() => {
    if (!hintEnabled) return;
    setHintVisible(true);
    const t = setTimeout(() => setHintVisible(false), HINT_DURATION_MS);
    return () => clearTimeout(t);
  }, [hintEnabled]);

  // Measure natural content height once on mount. We use it as the
  // baseline — pixels shrunk BELOW this trigger squeeze levels.
  useLayoutEffect(() => {
    if (!wrapperRef.current) return;
    naturalHeightRef.current = wrapperRef.current.offsetHeight;
  }, []);

  // Recompute squeeze level whenever currentHeight changes.
  useEffect(() => {
    if (currentHeight === null || naturalHeightRef.current === null) {
      setSqueezeLevel(0);
      return;
    }
    const shrunkBy = naturalHeightRef.current - currentHeight;
    if (shrunkBy >= SQUEEZE_2_THRESHOLD_PX) {
      setSqueezeLevel(2);
    } else if (shrunkBy >= SQUEEZE_1_THRESHOLD_PX) {
      setSqueezeLevel(1);
    } else {
      setSqueezeLevel(0);
    }
  }, [currentHeight]);

  // Drag handlers — Pointer Events cover mouse + touch + pen in one path.
  // DRAGGING UP = composer grows = chat history shrinks (negative dy).
  // DRAGGING DOWN = composer shrinks = items start disappearing (positive dy).
  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragStartY.current = e.clientY;
    // Start from current rendered height — if we haven't overridden yet,
    // capture the natural height as the baseline.
    dragStartHeight.current = currentHeight !== null
      ? currentHeight
      : (wrapperRef.current?.offsetHeight || 200);
    dragging.current = true;
    e.preventDefault();
  };

  const onPointerMove = (e) => {
    if (!dragging.current) return;
    const dy = e.clientY - dragStartY.current;
    // Inverted: dragging DOWN (positive dy) should SHRINK the composer
    // (since the composer is below the handle).
    const next = dragStartHeight.current - dy;
    // Floor: ensure at least the textarea + send + a little padding
    // remain visible. Cap: don't let composer eat more than 75% of
    // viewport (keeps chat history at least partially scrollable).
    const minPx = 90;
    const maxPx = Math.max(200, Math.round((window.innerHeight || 600) * 0.75));
    setCurrentHeight(Math.max(minPx, Math.min(maxPx, next)));
  };

  const onPointerUp = (e) => {
    if (!dragging.current) return;
    dragging.current = false;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    // Session-only: no API call. State persists across re-renders but
    // resets on full page reload. By design.
  };

  // Style for the wrapper div. When height is null, fall back to auto
  // sizing — this is also the snap-back state after page reload.
  const wrapperStyle = currentHeight !== null
    ? { ...style, height: `${currentHeight}px`, overflow: "hidden" }
    : style;

  return (
    <div
      className={`sticky bottom-0 ${className}`}
      style={{
        // Feb 16 2026 (Randy) — COMPLETELY TRANSPARENT composer area on
        // the public Q&A. No frame paint whatsoever; the composer's
        // inner card (ComposerResizable) has its own tpl-surface bg, so
        // only that opaque pill is visible over the canvas.
        // Padding reduced 95% per Randy (iOS keyboard-gap ask): dropped
        // the 0.75rem extra on top of safe-area — now ONLY iOS home-
        // indicator inset remains. On keyboard-open iOS resolves env()
        // to 0, so the composer sits flush against the keyboard.
        background: "transparent",
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
      }}
      data-testid="composer-area-splitter"
    >
      {/* Drag handle — replaces the old `border-t`. 8px tall hitbox so it's
          comfortable to grab on touchscreens. The thin visible line sits
          centered inside. Whole area cursor-ns-resize on hover. */}
      <div
        role="separator"
        aria-orientation="horizontal"
        aria-label="Drag to resize composer area"
        data-testid="composer-area-divider"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        className="relative w-full h-2 cursor-ns-resize touch-none select-none group"
      >
        {/* Visible line — Feb 16 2026 (Randy) — border color transparent so
            the divider is structurally there (still draggable at h-2 hitbox)
            but visually invisible over the canvas. */}
        <div
          className="absolute left-0 right-0 top-1/2 -translate-y-1/2 border-t border-transparent group-hover:border-[#4A5D4E]/40 transition-colors"
          style={{ height: 0 }}
        />
        {/* ↕ teaching hint — fades in for 3s on first paint when enabled. */}
        {hintEnabled && (
          <div
            data-testid="composer-divider-hint"
            className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 px-2 py-0.5 rounded-full border tpl-border pointer-events-none transition-opacity duration-700 ${
              hintVisible ? "opacity-90" : "opacity-0"
            } group-hover:opacity-100`}
            style={{ background: "var(--tpl-surface, #fff)", color: "var(--tpl-muted, #6B6A68)", fontSize: "10px", lineHeight: 1 }}
            aria-hidden="true"
          >
            ↕
          </div>
        )}
        {/* Persistent tiny grip on hover (always shown post-hint window) —
            mirrors the textarea handle so users associate the two. */}
        {!hintEnabled && (
          <div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 px-2 py-0.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
            style={{ background: "var(--tpl-surface, #fff)", color: "var(--tpl-muted, #6B6A68)", fontSize: "9px" }}
            aria-hidden="true"
          >
            ⋯
          </div>
        )}
      </div>

      <div ref={wrapperRef} style={wrapperStyle} data-testid="composer-area-content">
        {typeof children === "function" ? children({ squeezeLevel }) : children}
      </div>
    </div>
  );
}
